import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { formatOccurrenceDate } from "helpers/blackouts"
import styles from "./index.module.css"

/**
 * A repeating blackout's rule and its next dates: "↻ Repeats daily until Nov 29, 2026 · 32 times"
 * then "Next: Thu, Oct 29 · Fri, Oct 30 · Sat, Oct 31 · …". Shared by the Settings list and the
 * calendar's day cards, so a repeat reads the same wherever it is opened. Each caller expands the
 * rule (`getRecurrencePreview`) from the day it knows; a preview without a `total` drops the count.
 */
const RecurrenceLine = ({ label, preview, nextCount }) => {
  const { t } = useTranslation()
  const next = preview?.next || []
  const last = preview?.last

  const countText = (() => {
    if (!preview || preview.total === null) return ""
    if (preview.isTotalCapped) return t("settings.blackout.repeat_times_capped", { defaultValue: "{{count}}+ times", count: preview.total })
    if (preview.total === 1) return t("settings.blackout.repeat_times_one", { defaultValue: "1 time" })
    return t("settings.blackout.repeat_times", { defaultValue: "{{count}} times", count: preview.total })
  })()
  // More to come when the rule never ends, or its last date is beyond the ones listed.
  const hasMore = next.length === nextCount && (!last || last.start_date > next[next.length - 1].start_date)

  return (
    <Flex align="flex-start" gap="var(--spacing-xxs)" className={styles.line}>
      <Icon name="Repeat" width={14} height={14} color="var(--color-primary)" />
      <Text variant="caption" size="lg" className={styles.text}>
        {label}
        {countText && ` · ${countText}`}
        {next.length > 0 && (
          <>
            <br />
            <span className={styles.label}>{t("settings.blackout.repeat_next", { defaultValue: "Next:" })}</span>{" "}
            {next.map(formatOccurrenceDate).join(" · ")}
            {hasMore && " · …"}
          </>
        )}
        {next.length === 0 && last && (
          <>
            <br />
            {t("settings.blackout.repeat_ended", { defaultValue: "Ended on {{date}}", date: formatOccurrenceDate(last) })}
          </>
        )}
      </Text>
    </Flex>
  )
}

RecurrenceLine.propTypes = {
  /** The rule in words, from `formatRecurrence`. */
  label: PropTypes.string.isRequired,
  /** How many dates `preview.next` was asked for; a full list means "…" may follow. */
  nextCount: PropTypes.number.isRequired,
  /** From `getRecurrencePreview`; null when the rule cannot be expanded. */
  preview: PropTypes.shape({
    next: PropTypes.arrayOf(PropTypes.shape({ start_date: PropTypes.string, end_date: PropTypes.string })),
    last: PropTypes.shape({ start_date: PropTypes.string, end_date: PropTypes.string }),
    total: PropTypes.number,
    isTotalCapped: PropTypes.bool
  })
}

export default RecurrenceLine
